import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, X, ChevronDown, ChevronUp } from "lucide-react";
import { clientContactRelApi, driverApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import {
  EligibilityStatusBadge,
  EligibilityReasons,
  PassportChip,
} from "@/components/EligibilityBadges";
import {
  UnavailabilityDateRangeBadge,
  AssignmentConflictBadge,
} from "@/components/rc10l/RC10lBadges";
import { MedicalExpiringDuringTourChip } from "@/components/rc10m/MedicalExpiringDuringTourChip";

/**
 * DAQ-1 Sub-Phase 3 · Reusable Driver Search Panel.
 *
 * Renders filters + eligibility-aware result table. Same component
 * used in three surfaces:
 *   - `DriversList` (standalone advanced search)
 *   - Tour Staffing "Find Drivers" modal (via `tourContext` prop)
 *   - Standalone use elsewhere in the app if needed
 *
 * Rendering is a thin projection over `GET /api/drivers/search` output.
 * The component never mutates rows — clicking a row navigates to the
 * driver record. Sub-Phase 4 will add the write path.
 */

const AVAILABILITY_STATUSES = [
  ["", "Any"],
  ["CONFIRMED_AVAILABLE", "Confirmed Available"],
  ["AVAILABILITY_UNKNOWN", "Availability Unknown"],
  ["UNAVAILABLE", "Unavailable"],
];

const PASSPORT_STATUSES = [
  ["", "Any"],
  ["VALID", "Valid"],
  ["EXPIRING_SOON", "Expiring Soon"],
  ["EXPIRED", "Expired"],
  ["MISSING", "Missing"],
];

const EMPLOYMENT_STATUSES = [
  ["", "Any"],
  ["active", "Active"],
  ["on_hold", "On Hold"],
  ["quit", "Quit"],
  ["terminated", "Terminated"],
];

const LICENSE_TYPES = [
  ["", "Any"],
  ["A", "Class A"],
  ["B", "Class B"],
  ["C", "Class C"],
  ["other", "Other"],
];

export default function DriverSearchPanel({
  tourContext = null,         // { tour_id, tour_number, customer_id, start_date, end_date } | null
  defaultOpen = true,
  onRowAction,                // (row) => void — optional CTA per row (e.g. "Assign")
  actionLabel = null,         // label for onRowAction button
  dense = false,              // renders more compactly for modal use
  // Correction Batch STEP 1 — Advanced Search co-driver visibility.
  // When provided, the results table exposes TWO actions per row so
  // ops can start either flow without pre-selecting a tour. Both open
  // the shared TourSelectorModal. Reuses `perm("fleet.assign")` server-
  // side — no new permission or route.
  onAssignDriver = null,      // (row) => void — [Assign Driver]
  onAddCoDriver = null,       // (row) => void — [Add Co-Driver]
}) {
  const tourAssignActions = !!(onAssignDriver || onAddCoDriver);
  const [expanded, setExpanded] = useState(defaultOpen);
  // Tour override toggles
  const [useOverride, setUseOverride] = useState(false);
  const [overrideStart, setOverrideStart] = useState("");
  const [overrideEnd, setOverrideEnd] = useState("");

  // Standalone filters
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState(null);  // {id, display_name}
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [licenseType, setLicenseType] = useState("");
  const [passportStatus, setPassportStatus] = useState("");
  const [availabilityStatus, setAvailabilityStatus] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("");
  const [includeIneligible, setIncludeIneligible] = useState(!!tourContext);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced client search — reuses CC1 selector semantics
  // ("Three Doors" → "3 Doors Down") for parity across the app.
  useEffect(() => {
    if (tourContext) return; // tour mode ignores standalone customer selector
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await clientContactRelApi.searchClients(customerQuery, 20);
        if (!cancel) setCustomerResults(r);
      } catch { /* ignore */ }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [customerQuery, tourContext]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (q) p.q = q;
    if (tourContext) {
      p.tour_id = tourContext.tour_id;
      if (useOverride && overrideStart && overrideEnd) {
        p.date_range_override_start = overrideStart;
        p.date_range_override_end = overrideEnd;
      }
      if (tourContext.bus_id) p.bus_id = tourContext.bus_id;
    } else {
      if (customer?.id) p.customer_id = customer.id;
      if (startDate) p.start_date = startDate;
      if (endDate) p.end_date = endDate;
    }
    if (licenseType) p.license_type = licenseType;
    if (passportStatus) p.passport_status = passportStatus;
    if (availabilityStatus) p.availability_status = availabilityStatus;
    if (employmentStatus) p.employment_status = employmentStatus;
    if (includeIneligible) p.include_ineligible = true;
    return p;
  }, [q, customer, startDate, endDate, licenseType, passportStatus,
      availabilityStatus, employmentStatus, includeIneligible,
      tourContext, useOverride, overrideStart, overrideEnd, page, pageSize]);

  const search = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await driverApi.search(params);
      setData(r);
    } catch (e) {
      setData(null);
      setError(apiError(e) || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-run search when tour context is set (users expect immediate results),
  // or when filters change. In standalone mode we still auto-run so operators
  // see the shortlist right away — they can refine filters live.
  useEffect(() => {
    search();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params]);

  const resetFilters = () => {
    setQ("");
    setCustomer(null); setCustomerQuery("");
    setStartDate(""); setEndDate("");
    setLicenseType(""); setPassportStatus("");
    setAvailabilityStatus(""); setEmploymentStatus("");
    setIncludeIneligible(!!tourContext);
    setUseOverride(false); setOverrideStart(""); setOverrideEnd("");
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="space-y-3" data-testid="driver-search-panel">
      {/* Filter panel */}
      <div className="bg-white border border-coach-silver rounded-md">
        <button type="button" onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-road-white text-left">
          <div className="flex items-center gap-2 text-[13px] text-carbon font-medium">
            <Filter size={13} className="text-coach-blue" />
            {tourContext
              ? `Find Drivers for Tour ${tourContext.tour_number || tourContext.tour_id?.slice(0, 8) || ""}`
              : "Advanced Driver Search"}
          </div>
          {expanded ? <ChevronUp size={14} className="text-steel" /> : <ChevronDown size={14} className="text-steel" />}
        </button>
        {expanded && (
          <div className="px-4 pb-4 pt-1 space-y-3 border-t border-coach-silver/60">
            {tourContext ? (
              <div className="text-[12px] text-steel space-y-1">
                <div>Client: <span className="text-carbon font-medium">{tourContext.customer_display_name || tourContext.customer_id?.slice(0, 8) || "—"}</span></div>
                <div>Default dates: <span className="tabular font-mono text-carbon">{tourContext.start_date || "—"} → {tourContext.end_date || "—"}</span></div>
                <label className="flex items-center gap-2 mt-1 text-[12px]">
                  <input type="checkbox" checked={useOverride} onChange={(e) => setUseOverride(e.target.checked)}
                         data-testid="search-use-override" />
                  <span>Use custom date range for this search only</span>
                </label>
                {useOverride && (
                  <div className="grid grid-cols-2 gap-2 max-w-md">
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-widest text-steel">Override Start</div>
                      <input type="date" value={overrideStart} onChange={(e) => setOverrideStart(e.target.value)}
                             data-testid="search-override-start"
                             className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]" />
                    </label>
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-widest text-steel">Override End</div>
                      <input type="date" value={overrideEnd} onChange={(e) => setOverrideEnd(e.target.value)}
                             data-testid="search-override-end"
                             className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]" />
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2 relative">
                  <div className="text-[10px] uppercase tracking-widest text-steel">Client (Artist)</div>
                  {customer ? (
                    <div className="border border-coach-blue bg-coach-blue/5 rounded-sm px-2 py-1 flex items-center justify-between text-[12.5px]">
                      <span className="text-carbon">{customer.display_name}</span>
                      <button type="button" onClick={() => { setCustomer(null); setCustomerQuery(""); }}
                              className="text-coach-blue text-[11px] hover:underline">Clear</button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search size={11} className="absolute left-2 top-2 text-steel" />
                        <input value={customerQuery}
                               onChange={(e) => { setCustomerQuery(e.target.value); setShowCustomerResults(true); }}
                               onFocus={() => setShowCustomerResults(true)}
                               placeholder="Search Clients (e.g. Three Doors → 3 Doors Down)"
                               data-testid="search-customer-input"
                               className="w-full border border-coach-silver rounded-sm pl-7 pr-2 py-1 text-[12.5px]" />
                      </div>
                      {showCustomerResults && customerResults.length > 0 && (
                        <div className="absolute z-20 mt-1 left-0 right-0 max-h-52 overflow-y-auto border border-coach-silver rounded-sm bg-white shadow-md"
                             data-testid="search-customer-results">
                          {customerResults.map((r) => (
                            <button type="button" key={r.id}
                                    onClick={() => { setCustomer(r); setShowCustomerResults(false); setCustomerQuery(""); }}
                                    data-testid={`search-customer-option-${r.id}`}
                                    className="w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-chrome-mist border-b border-coach-silver/40 last:border-0">
                              {r.display_name}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-widest text-steel">Start Date</div>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                         data-testid="search-start-date"
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]" />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-widest text-steel">End Date</div>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                         data-testid="search-end-date"
                         className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]" />
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-steel">Name</div>
                <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                       placeholder="Search"
                       data-testid="search-q"
                       className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-steel">License Type</div>
                <select value={licenseType} onChange={(e) => setLicenseType(e.target.value)}
                        data-testid="search-license-type"
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px] bg-white">
                  {LICENSE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-steel">Passport</div>
                <select value={passportStatus} onChange={(e) => setPassportStatus(e.target.value)}
                        data-testid="search-passport"
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px] bg-white">
                  {PASSPORT_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-steel">Availability</div>
                <select value={availabilityStatus} onChange={(e) => setAvailabilityStatus(e.target.value)}
                        data-testid="search-availability"
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px] bg-white">
                  {AVAILABILITY_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-steel">Employment</div>
                <select value={employmentStatus} onChange={(e) => setEmploymentStatus(e.target.value)}
                        data-testid="search-employment"
                        className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px] bg-white">
                  {EMPLOYMENT_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-[12px] text-steel">
                <input type="checkbox" checked={includeIneligible}
                       onChange={(e) => setIncludeIneligible(e.target.checked)}
                       data-testid="search-include-ineligible" />
                <span>Include ineligible (hard-blocked) drivers</span>
              </label>
              <button type="button" onClick={resetFilters}
                      data-testid="search-reset"
                      className="text-[12px] text-coach-blue hover:underline">
                Reset filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white border border-coach-silver rounded-md">
        <div className="flex items-baseline justify-between px-4 py-2 border-b border-coach-silver">
          <div className="text-[12.5px] text-carbon">
            {loading ? "Searching…"
              : data ? `${data.total.toLocaleString()} driver${data.total === 1 ? "" : "s"} matched`
                    : "—"}
          </div>
          {data?.applied_context && (
            <div className="text-[11px] text-steel tabular font-mono hidden md:block">
              {data.applied_context.customer_id
                ? <span data-testid="applied-customer">client={data.applied_context.customer_id.slice(0, 8)}</span>
                : null}
              {data.applied_context.start_date && (
                <span className="ml-2" data-testid="applied-date-range">
                  {data.applied_context.start_date} → {data.applied_context.end_date}
                </span>
              )}
            </div>
          )}
        </div>
        {error && (
          <div className="p-4 text-[12.5px] text-critical-red bg-critical-red/5" data-testid="search-error">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="hidden md:table w-full text-[13px]" data-testid="search-results-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2 w-40">Status</th>
                <th className="text-left px-4 py-2">Driver</th>
                {/* RC4 · ITEM 8 — Driver # column hidden in Advanced
                    Search. Identifier is still returned by the API
                    and used for stable data-testids. */}
                <th className="text-left px-4 py-2 w-24">CDL</th>
                <th className="text-left px-4 py-2 w-40">Passport</th>
                <th className="text-left px-4 py-2">Reasons</th>
                {onRowAction && <th className="w-24 px-4 py-2"></th>}
                {tourAssignActions && <th className="w-56 px-4 py-2 text-right"></th>}
              </tr>
            </thead>
            <tbody>
              {data?.results?.map((row) => {
                const d = row.driver; const el = row.eligibility;
                return (
                  <tr key={d.id} data-testid={`search-row-${d.driver_number}`}
                      className="border-t border-coach-silver/60 hover:bg-road-white">
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <EligibilityStatusBadge status={el.overall_status} />
                        <UnavailabilityDateRangeBadge eligibility={el} testid={`rc10l-badge-unavailable-${d.id}`} />
                        <AssignmentConflictBadge eligibility={el} testid={`rc10l-badge-assignment-conflict-${d.id}`} />
                        <MedicalExpiringDuringTourChip eligibility={el} testid={`rc10m-chip-med-expire-${d.id}`} />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Link to={`/drivers/${d.id}`} className="text-coach-blue hover:underline font-medium">
                        {d.display_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-steel">{d.cdl_class || "—"}</td>
                    <td className="px-4 py-2"><PassportChip status={el.passport_status} /></td>
                    <td className="px-4 py-2"><EligibilityReasons eligibility={el} compact /></td>
                    {onRowAction && (
                      <td className="px-4 py-2 text-right">
                        <button
                          data-testid={`search-row-action-${d.driver_number}`}
                          onClick={() => onRowAction(row)}
                          aria-disabled={el.hard_blocked ? "true" : undefined}
                          title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                          className={
                            "px-2 py-1 text-[12px] rounded-sm text-white " +
                            (el.hard_blocked
                              ? "bg-coach-blue/70 border border-amber-hold hover:bg-coach-blue"
                              : "bg-coach-blue hover:bg-deep-road-navy")
                          }>
                          {el.hard_blocked && (
                            <span
                              aria-hidden="true"
                              data-testid={`search-row-blocked-indicator-${d.driver_number}`}
                              className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle"
                            />
                          )}
                          {actionLabel || "Assign"}
                        </button>
                      </td>
                    )}
                    {tourAssignActions && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          {onAssignDriver && (
                            <button
                              type="button"
                              data-testid={`driver-row-assign-${d.id}`}
                              onClick={() => onAssignDriver(row)}
                              aria-disabled={el.hard_blocked ? "true" : undefined}
                              title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                              className={
                                "px-2 py-1 text-[11.5px] rounded-sm text-white " +
                                (el.hard_blocked
                                  ? "bg-coach-blue/70 border border-amber-hold hover:bg-coach-blue"
                                  : "bg-coach-blue hover:bg-deep-road-navy")
                              }>
                              {el.hard_blocked && (
                                <span aria-hidden="true"
                                  data-testid={`driver-row-assign-blocked-indicator-${d.id}`}
                                  className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle" />
                              )}
                              Assign Driver
                            </button>
                          )}
                          {onAddCoDriver && (
                            <button
                              type="button"
                              data-testid={`driver-row-add-co-driver-${d.id}`}
                              onClick={() => onAddCoDriver(row)}
                              aria-disabled={el.hard_blocked ? "true" : undefined}
                              title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                              className={
                                "px-2 py-1 text-[11.5px] rounded-sm border " +
                                (el.hard_blocked
                                  ? "border-amber-hold text-coach-blue hover:bg-coach-blue/5"
                                  : "border-coach-blue text-coach-blue hover:bg-coach-blue/10")
                              }>
                              {el.hard_blocked && (
                                <span aria-hidden="true"
                                  data-testid={`driver-row-co-driver-blocked-indicator-${d.id}`}
                                  className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle" />
                              )}
                              Add Co-Driver
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {data && data.results.length === 0 && (
                <tr>
                  <td colSpan={(onRowAction ? 6 : 5) + (tourAssignActions ? 1 : 0)} className="text-center text-steel py-8" data-testid="search-empty">
                    No drivers match these filters.
                  </td>
                </tr>
              )}
              {!data && !error && !loading && (
                <tr><td colSpan={(onRowAction ? 6 : 5) + (tourAssignActions ? 1 : 0)} className="text-center text-steel py-8">—</td></tr>
              )}
            </tbody>
          </table>

          {/* Mobile cards (375px / 480px friendly) */}
          <div className="md:hidden divide-y divide-coach-silver/60" data-testid="search-mobile-list">
            {data?.results?.map((row) => {
              const d = row.driver; const el = row.eligibility;
              return (
                <div key={d.id} data-testid={`search-card-${d.driver_number}`} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/drivers/${d.id}`}
                            className="text-[13.5px] text-coach-blue hover:underline font-medium block truncate">
                        {d.display_name}
                      </Link>
                      <div className="text-[11px] text-steel">CDL {d.cdl_class || "—"}</div>
                    </div>
                    <EligibilityStatusBadge status={el.overall_status} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <UnavailabilityDateRangeBadge eligibility={el} testid={`rc10l-badge-unavailable-card-${d.id}`} />
                    <AssignmentConflictBadge eligibility={el} testid={`rc10l-badge-assignment-conflict-card-${d.id}`} />
                    <MedicalExpiringDuringTourChip eligibility={el} testid={`rc10m-chip-med-expire-card-${d.id}`} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                    <PassportChip status={el.passport_status} />
                  </div>
                  <div className="mt-2">
                    <EligibilityReasons eligibility={el} compact />
                  </div>
                  {onRowAction && (
                    <div className="mt-2 text-right">
                      <button
                        data-testid={`search-card-action-${d.driver_number}`}
                        onClick={() => onRowAction(row)}
                        aria-disabled={el.hard_blocked ? "true" : undefined}
                        title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                        className={
                          "px-2 py-1 text-[12px] rounded-sm text-white " +
                          (el.hard_blocked
                            ? "bg-coach-blue/70 border border-amber-hold"
                            : "bg-coach-blue hover:bg-deep-road-navy")
                        }>
                        {el.hard_blocked && (
                          <span aria-hidden="true"
                            className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle" />
                        )}
                        {actionLabel || "Assign"}
                      </button>
                    </div>
                  )}
                  {tourAssignActions && (
                    <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                      {onAssignDriver && (
                        <button
                          type="button"
                          data-testid={`driver-card-assign-${d.id}`}
                          onClick={() => onAssignDriver(row)}
                          aria-disabled={el.hard_blocked ? "true" : undefined}
                          title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                          className={
                            "px-2 py-1 text-[11.5px] rounded-sm text-white " +
                            (el.hard_blocked
                              ? "bg-coach-blue/70 border border-amber-hold"
                              : "bg-coach-blue hover:bg-deep-road-navy")
                          }>
                          {el.hard_blocked && (
                            <span aria-hidden="true"
                              className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle" />
                          )}
                          Assign Driver
                        </button>
                      )}
                      {onAddCoDriver && (
                        <button
                          type="button"
                          data-testid={`driver-card-add-co-driver-${d.id}`}
                          onClick={() => onAddCoDriver(row)}
                          aria-disabled={el.hard_blocked ? "true" : undefined}
                          title={el.hard_blocked ? "This driver is blocked — click to see why" : undefined}
                          className={
                            "px-2 py-1 text-[11.5px] rounded-sm border " +
                            (el.hard_blocked
                              ? "border-amber-hold text-coach-blue"
                              : "border-coach-blue text-coach-blue hover:bg-coach-blue/10")
                          }>
                          {el.hard_blocked && (
                            <span aria-hidden="true"
                              className="inline-block w-1.5 h-1.5 rounded-full bg-amber-hold mr-1 align-middle" />
                          )}
                          Add Co-Driver
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {data && data.results.length === 0 && (
              <div className="text-center text-steel text-[13px] py-6">No drivers match these filters.</div>
            )}
          </div>
        </div>
        {data && data.total > pageSize && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-coach-silver text-[12px]">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="search-prev-page"
                    className="text-coach-blue disabled:text-steel">← Prev</button>
            <span className="text-steel">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                    data-testid="search-next-page"
                    className="text-coach-blue disabled:text-steel">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
